/**
 * What the homepage says when a cycle has just closed.
 *
 * One resolver serves every journey. The wording for an ending is decided from
 * the cycle's own context — its family, the patient's role and the recorded
 * ending — so a tone change lands in one place rather than in sixteen journey
 * definitions.
 *
 * It returns translation keys, never literal strings: journey definitions carry
 * English inline, so the only way shared copy reaches French is by resolving at
 * render through `useTranslations`.
 *
 * It decides nothing clinical. `resolvePostProcedureOutcomeState` in
 * `@src/packages/cycles/presenters/postProcedureOutcomeState` remains the
 * authority on what a result means; this maps an already-resolved ending onto
 * words.
 */

import type { CycleType } from "../../cycles/hooks/useCycles";
import {
  resolvePostProcedureOutcomeClinicalState,
  type PostProcedureOutcomeResult,
} from "../../cycles/presenters/postProcedureOutcomeState";
import type { HomepageGender, HomepageGoal } from "../homepage.types";
import type { Pathway } from "../plan/plan.types";

/**
 * What kind of ending a cycle has. Preservation and donation cycles never
 * produce a pregnancy result, so they are not "negative" — they complete.
 */
export type CycleClosingFamily =
  "conceiving" | "preservation" | "donation" | "surrogacy";

/**
 * The patient's own relationship to the ending. Someone beside another person's
 * treatment shares the result without having carried it, and the copy must not
 * address them as though they did.
 */
export type CycleClosingRole = "primary" | "supporting";

/**
 * The ending itself. `complete` belongs to preservation and donation, where
 * finishing *is* the outcome. `unresolved` covers uncertain, unavailable and
 * conflicting records — never presented as positive or negative.
 */
export type CycleClosingEnding =
  | "pregnancy"
  | "no_pregnancy"
  | "loss"
  | "cancelled"
  | "complete"
  | "unresolved";

export type CycleClosingSlide = {
  titleKey: string;
  bodyKey: string;
};

export type CycleClosingContent = {
  family: CycleClosingFamily;
  role: CycleClosingRole;
  ending: CycleClosingEnding;
  slides: CycleClosingSlide[];
  /**
   * Whether the surface may congratulate. False for every ending except a
   * recorded pregnancy, so no tone change can accidentally congratulate a loss.
   */
  mayCelebrate: boolean;
};

const I18N_ROOT = "homepage.cycle_closing";

/**
 * Matched as strings rather than against `CycleType` so a caller can pass the
 * raw value it holds. Every ending arrives here from either a cycle record or a
 * dashboard field, and narrowing at each call site only spreads the same check
 * around.
 */
const PRESERVATION_CYCLE_TYPES: ReadonlySet<string> = new Set<CycleType>([
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

/**
 * Resolve the family.
 *
 * Donation is read from `goal`, never from `pathway`: `with-donor` means the
 * patient is *using* donor gametes — a recipient on a conceiving journey
 * (`journeys/ivf-donor.ts` is `goal: "pregnancy"`, `pathway: "with-donor"`) —
 * whereas an actual donor is `pathway: "solo"` with `goal: "egg-donation"` or
 * `"sperm-donation"` (`journeys/egg-donation.ts`, `journeys/sperm-donation.ts`).
 *
 * Reading donation off `with-donor` inverts both directions: because the
 * donation family short-circuits before the result is read, it would tell a
 * recipient who miscarried "your donation is complete — thank you".
 *
 * Surrogacy stays on `pathway`: `with-surrogate` is the intended parent, which
 * is who that copy addresses.
 */
function resolveFamily({
  cycleType,
  pathway,
  goal,
}: {
  cycleType: string | null | undefined;
  pathway: Pathway | null | undefined;
  goal: HomepageGoal | null | undefined;
}): CycleClosingFamily {
  if (goal === "egg-donation" || goal === "sperm-donation") return "donation";
  if (pathway === "with-surrogate") return "surrogacy";
  if (cycleType && PRESERVATION_CYCLE_TYPES.has(cycleType)) {
    return "preservation";
  }
  return "conceiving";
}

/**
 * `supporting` means the patient is beside someone else's treatment — the male
 * side of a conceiving or surrogacy journey, where the result is shared but the
 * treatment was not theirs.
 *
 * Preservation and donation are their own cycle, so they stay `primary`.
 * Omitting donation here would label a sperm donor "supporting" on the cycle he
 * completed himself.
 */
function resolveRole({
  gender,
  family,
  viewerIsSupporting,
}: {
  gender: HomepageGender | null | undefined;
  family: CycleClosingFamily;
  /**
   * True when the viewer is beside the treatment rather than in it — a connected
   * partner. Authoritative when known, because gender is only a proxy for it:
   * inferring from gender alone addresses a female partner as though she carried
   * the cycle.
   */
  viewerIsSupporting?: boolean;
}): CycleClosingRole {
  // Preservation and donation are the viewer's own cycle whichever way they are
  // reached, so they stay primary. Omitting that would label a sperm donor
  // "supporting" on the cycle he completed himself.
  if (family === "preservation" || family === "donation") return "primary";
  if (viewerIsSupporting) return "supporting";
  if (gender !== "male") return "primary";
  return "supporting";
}

/**
 * Map the recorded outcome onto an ending.
 *
 * Preservation and donation have no pregnancy result to read, so unless the
 * cycle was cancelled they are `complete`. Everything that is not an explicit
 * pregnancy, non-pregnancy, loss or cancellation stays `unresolved` — the same
 * fail-closed default the outcome presenter uses.
 */
function resolveEnding({
  family,
  outcomeResult,
  outcomeResultStatus,
  outcomeVerificationStatus,
  outcomeRecordStatus,
}: {
  family: CycleClosingFamily;
  outcomeResult: PostProcedureOutcomeResult;
  outcomeResultStatus?: string | null;
  outcomeVerificationStatus?: string | null;
  outcomeRecordStatus?: string | null;
}): CycleClosingEnding {
  const clinicalState = resolvePostProcedureOutcomeClinicalState({
    type: outcomeResult,
    resultStatus: outcomeResultStatus,
    verificationStatus: outcomeVerificationStatus,
    recordStatus: outcomeRecordStatus,
  });

  if (family === "preservation" || family === "donation") {
    if (outcomeResult === null) return "complete";
    if (clinicalState === "cancelled") return "cancelled";
    return clinicalState === "unresolved" ? "unresolved" : "complete";
  }

  switch (clinicalState) {
    case "cancelled":
      return "cancelled";
    case "adverse":
      return "loss";
    case "pregnancy":
      return "pregnancy";
    case "no_pregnancy":
      return "no_pregnancy";
    default:
      return "unresolved";
  }
}

/**
 * Only these combinations have their own supporting-role words. Everything else
 * falls back to the shared wording for the ending, so adding a journey can never
 * silently produce an untranslated key.
 */
const ROLE_SPECIFIC = new Set([
  "conceiving.pregnancy",
  "conceiving.no_pregnancy",
  "conceiving.loss",
  "surrogacy.pregnancy",
  "surrogacy.no_pregnancy",
  "surrogacy.loss",
]);

/**
 * Which endings carry a third slide.
 *
 * A cancelled cycle and an unresolved result have nothing settled to point
 * onward to, so they end on the second slide rather than manufacturing a
 * "what's next" that would imply a decision the patient has not been given.
 */
const ENDINGS_WITH_NEXT_SLIDE = new Set<CycleClosingEnding>([
  "pregnancy",
  "no_pregnancy",
  "loss",
  "complete",
]);

function endingCopyKeys({
  family,
  ending,
  role,
}: {
  family: CycleClosingFamily;
  ending: CycleClosingEnding;
  role: CycleClosingRole;
}): { title: string; body: string } {
  const title =
    ending === "pregnancy"
      ? "positive_result"
      : ending === "no_pregnancy"
        ? "cycle_is_complete"
        : ending === "loss"
          ? "cycle_ended_in_loss"
          : ending === "cancelled"
            ? "cycle_was_cancelled"
            : ending === "complete"
              ? family === "donation"
                ? "donation_is_complete"
                : "preservation_reached_completion"
              : "result_is_not_settled";

  let body: string;
  if (ending === "pregnancy") {
    body =
      role === "supporting"
        ? "positive_result_shared_clinic_guidance"
        : "positive_result_clinic_guidance";
  } else if (ending === "no_pregnancy") {
    body =
      role === "supporting"
        ? "no_pregnancy_support_context"
        : "no_pregnancy_context";
  } else if (ending === "loss") {
    body =
      role === "supporting" ? "loss_support_guidance" : "loss_care_guidance";
  } else if (ending === "cancelled") {
    body =
      family === "preservation"
        ? "preservation_cancellation_guidance"
        : family === "donation"
          ? "donation_cancellation_guidance"
          : "cancelled_cycle_guidance";
  } else if (ending === "complete") {
    body =
      family === "donation"
        ? "donation_acknowledgement"
        : "stored_materials_are_ready";
  } else {
    body =
      family === "preservation"
        ? "preservation_result_guidance"
        : family === "donation"
          ? "donation_result_guidance"
          : "unresolved_result_guidance";
  }

  return { title, body };
}

const SLIDE_COPY_KEYS: Record<
  CycleClosingFamily,
  {
    now: { title: string; body: string };
    next: { title: string; body: string };
  }
> = {
  conceiving: {
    now: {
      title: "nothing_needs_deciding_today",
      body: "saved_result_can_wait",
    },
    next: { title: "cycle_history", body: "cycle_history_guidance" },
  },
  preservation: {
    now: {
      title: "nothing_needs_deciding_today",
      body: "stored_materials_can_wait",
    },
    next: { title: "cryo_vault", body: "cryo_vault_guidance" },
  },
  donation: {
    now: {
      title: "nothing_needs_deciding_today",
      body: "donation_needs_no_action",
    },
    next: { title: "cycle_record", body: "cycle_record_guidance" },
  },
  surrogacy: {
    now: {
      title: "nothing_needs_deciding_today",
      body: "saved_result_can_wait",
    },
    next: { title: "cycle_history", body: "cycle_history_guidance" },
  },
};

/**
 * Whether this ending has stopped being worth greeting.
 *
 * Every ending is retired by something that actually happened, never by a timer:
 * a timer would clear a result the patient had not acted on, and would keep one
 * that was still the most relevant thing on their Home.
 *
 * - a pregnancy is retired once follow-up genuinely starts, which is what the
 *   patient is waiting for — not the positive result itself;
 * - a non-pregnancy, a loss and a cancellation are retired when a new cycle
 *   opens, because that is the patient deciding what comes next, in their own
 *   time and without being asked to;
 * - an unresolved result is retired when it resolves;
 * - preservation and donation are never retired. Finishing is their outcome, so
 *   there is no later event that makes it stale.
 */
export function resolveCycleClosingRetired({
  ending,
  family,
  hasOpenCycle,
  pregnancyFollowUpStarted,
}: {
  ending: CycleClosingEnding;
  family: CycleClosingFamily;
  hasOpenCycle: boolean;
  pregnancyFollowUpStarted: boolean;
}): boolean {
  if (family === "preservation" || family === "donation") return false;
  if (ending === "pregnancy") return pregnancyFollowUpStarted;
  if (ending === "unresolved") return false;
  return hasOpenCycle;
}

export function resolveCycleClosingContent({
  cycleType,
  pathway,
  gender,
  goal,
  outcomeResult,
  outcomeResultStatus,
  outcomeVerificationStatus,
  outcomeRecordStatus,
  viewerIsSupporting,
}: {
  cycleType: string | null | undefined;
  pathway: Pathway | null | undefined;
  gender: HomepageGender | null | undefined;
  goal: HomepageGoal | null | undefined;
  outcomeResult: PostProcedureOutcomeResult;
  /** The API status that qualifies the recorded result, when present. */
  outcomeResultStatus?: string | null;
  /** The API verification state that qualifies the recorded result, when present. */
  outcomeVerificationStatus?: string | null;
  /** The API record status; a row retracted as `entered_in_error` is not an ending. */
  outcomeRecordStatus?: string | null;
  /** True when the viewer is a connected partner rather than the patient. */
  viewerIsSupporting?: boolean;
}): CycleClosingContent {
  const family = resolveFamily({ cycleType, pathway, goal });
  const role = resolveRole({ gender, family, viewerIsSupporting });
  const ending = resolveEnding({
    family,
    outcomeResult,
    outcomeResultStatus,
    outcomeVerificationStatus,
    outcomeRecordStatus,
  });

  const roleSuffix =
    role === "supporting" && ROLE_SPECIFIC.has(`${family}.${ending}`)
      ? ".supporting"
      : "";
  const endingBase = `${I18N_ROOT}.${family}.${ending}${roleSuffix}`;
  const familyBase = `${I18N_ROOT}.slides.${family}`;
  const endingKeys = endingCopyKeys({ family, ending, role });
  const slideKeys = SLIDE_COPY_KEYS[family];

  const slides: CycleClosingSlide[] = [
    // What happened. Keyed to the ending, so a loss, a cancellation and an
    // unresolved result cannot share one another's words.
    {
      titleKey: `${endingBase}.${endingKeys.title}`,
      bodyKey: `${endingBase}.${endingKeys.body}`,
    },
    // What is true now. Keyed to the family, because "nothing needs deciding
    // today" reads the same across a family's endings.
    {
      titleKey: `${familyBase}.now.${slideKeys.now.title}`,
      bodyKey: `${familyBase}.now.${slideKeys.now.body}`,
    },
  ];

  if (ENDINGS_WITH_NEXT_SLIDE.has(ending)) {
    slides.push({
      titleKey: `${familyBase}.next.${slideKeys.next.title}`,
      bodyKey: `${familyBase}.next.${slideKeys.next.body}`,
    });
  }

  return {
    family,
    role,
    ending,
    slides,
    mayCelebrate: ending === "pregnancy",
  };
}
